// client/src/components/tax/MemberTaxDocuments.jsx
//
// /:tenant/tax-documents — end-user Tax Center in the member portal: year
// selector, the member's issued documents, and one "Download my tax
// documents" button (a ZIP streamed by the server). Linked from the
// "Your <year> tax documents are ready" email / push and from My Tickets.
//
// API: /api/tax/me (server/routes/taxMemberRoutes.js); member cookie session.

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaDownload } from 'react-icons/fa';
import { getMemberTaxOverview, getMemberDocuments, memberDownload } from '../../services/taxApi';
import '../analytics/PerformancePanel.css';
import './Tax.css';

export default function MemberTaxDocuments() {
  const { t } = useTranslation();
  const { tenant } = useParams();
  const [search, setSearch] = useSearchParams();
  const [state, setState] = useState('loading'); // loading | ready | unauth | error
  const [overview, setOverview] = useState(null);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const year = Number(search.get('year')) || (overview && overview.years[0]) || null;

  const load = useCallback(async () => {
    try {
      const o = await getMemberTaxOverview();
      setOverview(o);
      setState('ready');
    } catch (err) {
      setState(err.status === 401 ? 'unauth' : 'error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (state !== 'ready' || !year) { setDocs([]); return; }
    getMemberDocuments(year).then((r) => setDocs(r.documents || [])).catch(() => setDocs([]));
  }, [state, year]);

  const download = async (path, name) => {
    setBusy(true);
    setError('');
    try { await memberDownload(path, name); } catch (err) { setError(err.message || t('tax.me.downloadError', 'Could not download.')); } finally { setBusy(false); }
  };

  return (
    <div className="tx-public">
      <div className="tx-public-inner">
        <section className="surface">
          <h1>{t('tax.me.title', 'My tax documents')}</h1>
          {overview && overview.organization && <p className="surface-muted perf-small">{overview.organization}</p>}
          {overview && <div className="note warn tx-disclaimer" role="note">{overview.disclaimer}</div>}
        </section>

        {state === 'loading' && <p>{t('common.loading', 'Loading…')}</p>}
        {state === 'unauth' && (
          <section className="surface">
            <p>{t('tax.me.signIn', 'Sign in with the email address your tax documents were sent to.')}</p>
            <Link className="lm-btn" to={`/${tenant}/memberLogin`}>{t('tax.me.signInButton', 'Sign in')}</Link>
          </section>
        )}
        {state === 'error' && <div className="note warn">{t('tax.me.loadError', 'Could not load your tax documents. Try again later.')}</div>}

        {state === 'ready' && overview && (
          <section className="surface" aria-labelledby="tx-me-docs">
            {overview.years.length === 0 ? (
              <p>{t('tax.me.none', 'You have no tax documents yet. They appear here once the organization issues them, usually in January.')}</p>
            ) : (
              <>
                <div className="tx-head">
                  <h4 id="tx-me-docs">{t('tax.me.forYear', 'Documents for {{year}}', { year })}</h4>
                  <label className="tx-year">
                    {t('tax.year', 'Tax year')}
                    <select value={year || ''} onChange={(e) => setSearch({ year: e.target.value }, { replace: true })}>
                      {overview.years.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </label>
                </div>
                <ul className="tx-doclist">
                  {docs.map((d) => (
                    <li key={d.id} className="tx-doc">
                      <div>
                        <strong>{d.title}</strong>
                        {d.status === 'corrected' && <span> · {t('tax.status.corrected', 'Corrected')}</span>}
                      </div>
                      <button type="button" className="lm-btn secondary" disabled={busy} onClick={() => download(`documents/${d.id}`, d.fileName)}>
                        {t('tax.docs.download', 'Download')}
                      </button>
                    </li>
                  ))}
                </ul>
                {docs.length > 0 && (
                  <div className="tx-actions" style={{ marginTop: 10 }}>
                    <button type="button" className="lm-btn" disabled={busy} onClick={() => download(`zip?year=${year}`, `${year}-tax-documents.zip`)}>
                      <FaDownload aria-hidden="true" /> {busy ? t('tax.me.preparing', 'Preparing…') : t('tax.downloadMine', 'Download my tax documents')}
                    </button>
                  </div>
                )}
                {error && <p className="tx-err" role="alert">{error}</p>}
              </>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
